import { makeLmsRequest } from '../service/LmsClient';
import { External, M } from '../../external';
import { LmsUtils } from '../utils/LmsUtils';
import { makeWebsiteRequest } from '../../Common/tools';
import { showLoadingPopup } from '../../Common/LoadingPopup.svelte';
import CircleAnalytics from '../popups/CircleAnalytics.svelte';
import { showActivateAddonUI } from '../../Billing/Addons/addons';
import { getLmsLocaleContext } from '../utils/LmsLocaleUtils';

export let LmsCircleUtils = {
  deleteCircle: async (circle) => {
    let result = await makeLmsRequest('/circle/' + circle.id, 'DELETE');
    if (result.ok) {
      LmsUtils.removeCachedCircle(circle.id);
    }
  },
  newCircleFromTemplate: async (templateId, name) => {
    return new Promise(function(resolve, reject) {
      showLoadingPopup(async (popup) => {
        let result = await makeLmsRequest(`/circle/${templateId}/duplicate`, 'POST', {
          name: name,
          products: {}
        });

        if (result.ok) {
          LmsUtils.clearCachedCircles();
          popup.close();
          resolve(await result.json());
        } else {
          resolve(null);
        }
      });
    });
  },
  duplicatePage: (circle, page) => {
    return new Promise(function(resolve, reject) {
      showLoadingPopup(async (popup) => {
        // first clone the product, then the page with the new product. If creating the page fails, delete the product.
        let newProductsMap = null;
        if (page.access?.type === 'PAID' && page.access.productId) {
          newProductsMap = await LmsUtils.cloneProducts([page.access.productId]);
          if (newProductsMap === null) {
            popup.close();
            resolve(null);
          }
        }

        let result = await makeLmsRequest(`/circle/${circle.id}/duplicate/page/${page.id}`, 'POST', {
          products: newProductsMap
        });

        if (result.ok) {
          popup.close();
          resolve(await result.json());
        } else if (newProductsMap) {
          try {
            // try to delete the newly created products if creating the circle fails
            await makeWebsiteRequest({
              action: 'deleteProduct',
              data: {
                ids: Object.values(newProductsMap).join(',')
              }
            });
          } catch (ex) {
          }
          popup.close();
          resolve(null);
        }
      });
    });
  },
  duplicateCircle: (circleDetailsOrUrl, then) => {
    return new Promise(function(resolve, reject) {
      showLoadingPopup(async (popup) => {
        let circleDetails;
        if (circleDetailsOrUrl.circle) {
          circleDetails = circleDetailsOrUrl;
        } else {
          circleDetails = await LmsUtils.getCircleDetailsByUrl(circleDetailsOrUrl);
        }

        // first clone the products, then the circle with the new products. If creating the circle fails, delete the products.
        let productIds = LmsUtils.getProductIds(circleDetails);
        let newProductsMap = null;
        if (productIds.length) {
          newProductsMap = await LmsUtils.cloneProducts(productIds);
          if (newProductsMap === null) {
            popup.close();
            resolve(null);
          }
        }

        let result = await makeLmsRequest('/circle/' + circleDetails.circle.id + '/duplicate', 'POST', {
          products: newProductsMap
        });

        if (result.ok) {
          LmsUtils.clearCachedCircles();
          popup.close();
          resolve(await result.json());
        } else if (newProductsMap) {
          try {
            // try to delete the newly created products if creating the circle fails
            await makeWebsiteRequest({
              action: 'deleteProduct',
              data: {
                ids: Object.values(newProductsMap).join(',')
              }
            });
          } catch (ex) {
          }
          popup.close();
          resolve(null);
        }
      });
    });
  },
  preview: (circle, group, page, isTemplate) => {
    let clientFacingUrl = LmsCircleUtils.clientFacingLink(circle, group, page, isTemplate);
    clientFacingUrl += (clientFacingUrl.includes('?') ? '&' : '?') + 'preview=__PREVIEW_ONLY&v=' + new Date().getTime();
    External.Tools.showWebsitePreviewPopup({
      url: clientFacingUrl,
      buttonText: M.SHOW_IN_NEW_TAB,
      onButtonAction: (pageUrl) => {
        let win = window.open(pageUrl, '_blank');
        win.focus();
      }
    });
  },
  clientFacingLink: (circle, group, page, isTemplate) => {
    let host;
    if (isTemplate || window.subdomain === 'www') {
      host = `${isTemplate ? 'template-editor' : window.subdomain}.${window.domain}`;
    } else {
      host = window.primaryDomain;
    }

    // Preview/public links must carry the active locale prefix (e.g. /de/community/...) so the
    // public page resolves the same locale the editor is in; default locale uses no prefix.
    let localePrefix = '';
    if (!isTemplate) {
      let localeContext = getLmsLocaleContext();
      if (localeContext.currentLocale && localeContext.currentLocale !== localeContext.defaultLocale) {
        localePrefix = '/' + localeContext.currentLocale;
      }
    }

    let url = `https://${host}${localePrefix}/${External.WebPlatform.lmsBase}/${circle.url}`;
    if (group) {
      url += '/' + group.url;
    }
    if (page) {
      url += '/' + page.url;
    }
    return url;
  },
  copyCircleUrl: function(circle) {
    let url = LmsCircleUtils.clientFacingLink(circle);
    External.Tools.copyToClipboard(url);
    External.Tools.showCornerMessage(1000, M.COPIED_TO_CLIPBOARD, External.Tools.TOOLTIP_TYPE_SUCCESS);
  },
  togglePublished: async (circle) => {
    let setPublished = !circle.published;
    let result = await makeLmsRequest('/circle/' + circle.id + '/' + (setPublished ? 'publish' : 'unpublish'), 'POST');
    if (result.ok) {
      circle.published = setPublished;
      LmsUtils.updateCachedCircle(circle);
      const message = circle.published ? M.CIRCLE_PUBLISHED : M.CIRCLE_UNPUBLISHED;
      External.Tools.showCornerMessage(1000, message, External.Tools.TOOLTIP_TYPE_SUCCESS);
    } else if (result.error === 'LIMITED') {
      await showActivateAddonUI('COMMUNITY', null, true, M.COMMUNITY_LIMITS_REACHED);
    }
  },
  showAnalytics: (fullCircleDetailsOrUrl, entity, type) => {
    External.Tools.showWindow({
      noTitle: true,
      noNav: true,
      width: 950,
      isModal: false,
      withDarkBackground: true,
      forceAtRight: External.showBuilderPopupsOnFullHeight,
      action: {
        onShow: function(popup, ui) {
          popup.bindComponent(
            new CircleAnalytics({
              target: popup.getContentArea(),
              props: {
                circleDetails: fullCircleDetailsOrUrl,
                entity: entity,
                type: type
              }
            })
          );
        }
      }
    });
  }
};
